import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, IconButton, Tooltip,
  List, ListItemButton, ListItemIcon, ListItemText, Avatar, Menu, MenuItem,
  Divider, useMediaQuery, useTheme, ButtonBase,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ShoppingCart as OrdersIcon,
  Inventory as ProductsIcon,
  Category as CategoryIcon,
  LocalOffer as CouponIcon,
  Warehouse as InventoryIcon,
  Image as BannerIcon,
  ViewAgenda as LayoutIcon,
  AccountBalance as BankIcon,
  ShoppingBag as AddonsIcon,
  People as UsersIcon,
  Store as StoreIcon,
  ContactMail as ContactIcon,
  DeliveryDining as DeliveryIcon,
  BarChart as AnalyticsIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { color, layout, radius, shadow } from '../theme/tokens';

const SIDEBAR_STORAGE_KEY = 'admin.sidebarCollapsed';

const NAV_GROUPS = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { label: 'Dashboard', icon: <DashboardIcon />, path: '/' },
      { label: 'Analytics', icon: <AnalyticsIcon />, path: '/analytics' },
    ],
  },
  {
    id: 'commerce',
    label: 'Commerce',
    items: [
      { label: 'Orders',        icon: <OrdersIcon />,   path: '/orders' },
      { label: 'Fast Delivery', icon: <DeliveryIcon />, path: '/fast-delivery' },
      { label: 'Enquiries',     icon: <ContactIcon />,  path: '/enquiries' },
    ],
  },
  {
    id: 'catalog',
    label: 'Catalog',
    items: [
      { label: 'Products',   icon: <ProductsIcon />,  path: '/products' },
      { label: 'Categories', icon: <CategoryIcon />,  path: '/categories' },
      { label: 'Inventory',  icon: <InventoryIcon />, path: '/inventory' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing & CMS',
    items: [
      { label: 'Homepage Layout', icon: <LayoutIcon />, path: '/homepage-layout' },
      { label: 'Banners', icon: <BannerIcon />, path: '/banners' },
      { label: 'Bank Offers', icon: <BankIcon />, path: '/bank-offers' },
      { label: 'Cart Impulse Add-Ons', icon: <AddonsIcon />, path: '/cart-addons' },
      { label: 'Coupons', icon: <CouponIcon />, path: '/coupons' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { label: 'Users',  icon: <UsersIcon />, path: '/users' },
      { label: 'Stores', icon: <StoreIcon />, path: '/stores' },
    ],
  },
];

const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

const BrandMark = ({ size = 32 }) => (
  <Box
    component="img"
    src="/Hotspot Logo smart (1) - Copy.png"
    alt="HotSpot"
    sx={{
      width: 'auto',
      height: size,
      flexShrink: 0,
      objectFit: 'contain',
    }}
  />
);

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === '1'
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const activeItem = useMemo(
    () => ALL_NAV_ITEMS.find((i) => i.path === location.pathname),
    [location.pathname]
  );

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const drawerWidth = collapsed ? layout.sidebarCollapsed : layout.sidebarExpanded;
  const showLabels = isMobile || !collapsed;

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: color.surface }}>
      {/* Brand */}
      <Box
        sx={{
          height: layout.headerHeight,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: showLabels ? 2 : 0,
          justifyContent: showLabels ? 'flex-start' : 'center',
          borderBottom: `1px solid ${color.divider}`,
        }}
      >
        <ButtonBase
          onClick={() => handleNavClick('/')}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, borderRadius: `${radius.md}px`, p: 0.5 }}
          aria-label="HotSpot Admin home"
        >
          <BrandMark />
          {showLabels && (
            <Box sx={{ textAlign: 'left' }}>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: color.ink[900], lineHeight: 1.2 }}>
                HotSpot
              </Typography>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 500, color: color.ink[400], letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                Admin
              </Typography>
            </Box>
          )}
        </ButtonBase>

        {!isMobile && showLabels && (
          <Tooltip title="Collapse sidebar">
            <IconButton
              size="small"
              onClick={() => setCollapsed(true)}
              sx={{ ml: 'auto' }}
              aria-label="Collapse sidebar"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5 }}>
        {NAV_GROUPS.map((group) => (
          <Box key={group.id} sx={{ mb: 1.5 }}>
            {showLabels && (
              <Typography
                sx={{
                  px: 2.5, mb: 0.5,
                  fontSize: '0.6875rem', fontWeight: 600,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: color.ink[400],
                }}
              >
                {group.label}
              </Typography>
            )}
            <List disablePadding sx={{ px: showLabels ? 1.25 : 1 }}>
              {group.items.map((item) => {
                const active = location.pathname === item.path;
                const button = (
                  <ListItemButton
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    selected={active}
                    sx={{
                      minHeight: 40,
                      mb: 0.25,
                      px: showLabels ? 1.25 : 0,
                      justifyContent: showLabels ? 'flex-start' : 'center',
                      position: 'relative',
                      color: active ? color.ink[900] : color.ink[600],
                      fontWeight: active ? 600 : 500,
                      '&:hover': { backgroundColor: color.ink[100] },
                      '&.Mui-selected': {
                        backgroundColor: color.primary[50],
                        color: color.ink[900],
                        '&:hover': { backgroundColor: color.primary[100] },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: showLabels ? -10 : 0,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 3,
                          height: 20,
                          borderRadius: 2,
                          backgroundColor: color.primary[500],
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: showLabels ? 34 : 'auto',
                        color: active ? color.primary[500] : color.ink[500],
                        justifyContent: 'center',
                        '& svg': { fontSize: 20 },
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {showLabels && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 'inherit', noWrap: true }}
                      />
                    )}
                  </ListItemButton>
                );

                return showLabels ? (
                  <React.Fragment key={item.path}>{button}</React.Fragment>
                ) : (
                  <Tooltip key={item.path} title={item.label} placement="right" arrow>
                    {button}
                  </Tooltip>
                );
              })}
            </List>
          </Box>
        ))}

        {!showLabels && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Tooltip title="Expand sidebar">
              <IconButton size="small" onClick={() => setCollapsed(false)} aria-label="Expand sidebar">
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Account footer */}
      <Box sx={{ flexShrink: 0, borderTop: `1px solid ${color.divider}`, p: showLabels ? 1.5 : 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: showLabels ? 1 : 0.5,
            borderRadius: `${radius.lg}px`,
            justifyContent: showLabels ? 'flex-start' : 'center',
          }}
        >
          <Avatar
            src={admin?.profile_pic || undefined}
            sx={{ width: 34, height: 34, bgcolor: color.ink[900], color: '#fff', fontSize: '0.8125rem' }}
          >
            {admin?.username?.[0]?.toUpperCase() || 'A'}
          </Avatar>
          {showLabels && (
            <>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: color.ink[900] }}>
                  {admin?.username || 'Admin'}
                </Typography>
                <Typography noWrap sx={{ fontSize: '0.6875rem', color: color.ink[500], textTransform: 'capitalize' }}>
                  {admin?.usertype || 'Administrator'}
                </Typography>
              </Box>
              <Tooltip title="Sign out">
                <IconButton size="small" onClick={handleLogout} aria-label="Sign out">
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        {!showLabels && (
          <Tooltip title="Sign out" placement="right" arrow>
            <IconButton size="small" onClick={handleLogout} sx={{ mt: 0.5 }} aria-label="Sign out">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: color.bg }}>
      {/* Sidebar — desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            borderRight: `1px solid ${color.border}`,
            bgcolor: color.surface,
            transition: 'width .18s ease',
            overflowX: 'hidden',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Sidebar — mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: layout.sidebarExpanded, boxSizing: 'border-box', border: 'none' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main column */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(8px)',
            color: color.ink[900],
            borderBottom: `1px solid ${color.border}`,
          }}
        >
          <Toolbar sx={{ minHeight: layout.headerHeight, gap: 1.5, px: { xs: 2, md: 3 } }}>
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' } }}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </IconButton>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontSize: { xs: '0.9375rem', md: '1rem' }, fontWeight: 600, color: color.ink[900], lineHeight: 1.3 }}
              >
                {activeItem?.label || 'Admin'}
              </Typography>
              <Typography
                noWrap
                sx={{ display: { xs: 'none', sm: 'block' }, fontSize: '0.75rem', color: color.ink[400] }}
              >
                Home / {activeItem?.label || 'Admin'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  pl: 1,
                  pr: 1.25,
                  py: 0.5,
                  borderRadius: `${radius.pill}px`,
                  border: `1px solid ${color.border}`,
                  bgcolor: color.surface,
                  cursor: 'pointer',
                  transition: 'border-color .15s ease, background-color .15s ease',
                  '&:hover': { borderColor: color.borderStrong, backgroundColor: color.ink[50] },
                }}
                onClick={(e) => setAnchorEl(e.currentTarget)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAnchorEl(e.currentTarget); }}
                aria-label="Account menu"
              >
                <Avatar sx={{ width: 28, height: 28, bgcolor: color.ink[900], color: '#fff', fontSize: '0.75rem' }}>
                  {admin?.username?.[0]?.toUpperCase() || 'A'}
                </Avatar>
                <Box sx={{ lineHeight: 1.2, maxWidth: 140 }}>
                  <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: color.ink[900] }}>
                    {admin?.username || 'Admin'}
                  </Typography>
                  <Typography noWrap sx={{ fontSize: '0.6875rem', color: color.ink[500], textTransform: 'capitalize' }}>
                    {admin?.usertype || 'Administrator'}
                  </Typography>
                </Box>
                <ExpandMoreIcon sx={{ fontSize: 18, color: color.ink[400] }} />
              </Box>

              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                aria-label="Account menu"
              >
                <Avatar sx={{ width: 30, height: 30, bgcolor: color.ink[900], color: '#fff', fontSize: '0.75rem' }}>
                  {admin?.username?.[0]?.toUpperCase() || 'A'}
                </Avatar>
              </IconButton>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 240, boxShadow: shadow.md } } }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: color.ink[900] }}>
                  {admin?.username || 'Admin'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: color.ink[500], mt: 0.25 }}>
                  {admin?.email || admin?.usertype || 'Administrator'}
                </Typography>
              </Box>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem onClick={handleLogout} sx={{ color: color.danger.text }}>
                <LogoutIcon fontSize="small" sx={{ mr: 1.25, fontSize: 18 }} /> Sign out
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box
          component="main"
          sx={{ flex: 1, px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 }, overflowX: 'hidden' }}
        >
          <Box sx={{ maxWidth: layout.contentMax, mx: 'auto', width: '100%' }}>{children}</Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
